import { Injectable, inject } from '@angular/core';
import { firstValueFrom, Subject } from 'rxjs';
import { ThemeMode, User } from '../interfaces/api-interfaces';
import { ApiService } from './api.service';
import { KeycloakService } from './keycloak.service';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private readonly THEME_KEY = 'aims-theme-preference';
  private readonly LIGHT_THEME = 'masasana';
  private readonly DARK_THEME = 'masasana-dark';

  private apiService = inject(ApiService);
  private keycloakService = inject(KeycloakService);

  // Event emitter for theme changes (listened to by BrandingService)
  private themeChanged$ = new Subject<void>();
  public readonly themeChanged = this.themeChanged$.asObservable();
  
  constructor() {
    this.initializeTheme();
  }

  /**
   * Initialize theme on app startup
   */
  initializeTheme(): void {
    const savedTheme = this.getThemePreference();
    this.applyTheme(savedTheme);
  }

  /**
   * Get current theme preference from localStorage
   */
  getThemePreference(): ThemeMode {
    const stored = localStorage.getItem(this.THEME_KEY);
    if (stored && ['light', 'dark', 'system'].includes(stored)) {
      return stored as ThemeMode;
    }
    return 'light'; // Default theme
  }

  /**
   * Set theme preference and apply it
   */
  setThemePreference(theme: ThemeMode): void {
    localStorage.setItem(this.THEME_KEY, theme);
    this.applyTheme(theme);
  }

  /**
   * Apply theme to document
   */
  private applyTheme(themeMode: ThemeMode): void {
    let actualTheme: string;

    switch (themeMode) {
      case 'light':
        actualTheme = this.LIGHT_THEME;
        break;
      case 'dark':
        actualTheme = this.DARK_THEME;
        break;
      case 'system':
        actualTheme = this.getSystemTheme();
        break;
      default:
        actualTheme = this.LIGHT_THEME;
    }

    document.documentElement.setAttribute('data-theme', actualTheme);

    // Emit event for BrandingService to reapply colors
    this.themeChanged$.next();
  }

  /**
   * Get system theme preference
   */
  private getSystemTheme(): string {
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return this.DARK_THEME;
    }
    return this.LIGHT_THEME;
  }

  /**
   * Listen for system theme changes
   */
  listenForSystemThemeChanges(): void {
    if (window.matchMedia) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      mediaQuery.addEventListener('change', () => {
        const currentPreference = this.getThemePreference();
        if (currentPreference === 'system') {
          this.applyTheme('system');
        }
      });
    }
  }

  /**
   * Get current applied theme (actual theme name, not preference)
   */
  getCurrentTheme(): string {
    return document.documentElement.getAttribute('data-theme') || this.LIGHT_THEME;
  }

  /**
   * Check if current theme is dark
   */
  isDarkTheme(): boolean {
    return this.getCurrentTheme() === this.DARK_THEME;
  }

  // =============================================================================
  // USER-BASED THEME METHODS
  // =============================================================================

  /**
   * Get theme preference from user object or fallback to localStorage
   */
  getUserTheme(user: User | null): ThemeMode {
    if (user?.theme) {
      return user.theme;
    }
    // Fallback to localStorage for existing users
    return this.getThemePreference();
  }

  /**
   * Update user theme preference via API and apply immediately
   */
  async updateUserTheme(user: User, theme: ThemeMode): Promise<User> {
    const updatedUser: User = {
      ...user,
      theme: theme
    };
    
    try {
      const result = await firstValueFrom(this.apiService.updateUserProfile(updatedUser));
      // Update the signal with new user data
      this.keycloakService.updateUser(result);
      // Apply theme immediately
      this.applyTheme(theme);
      return result;
    } catch (error) {
      console.error('Failed to update user theme:', error);
      // Still apply theme locally even if API fails
      this.applyTheme(theme);
      throw error;
    }
  }

  /**
   * Initialize theme from user data (called after user loads)
   */
  initializeFromUser(user: User | null): void {
    const theme = this.getUserTheme(user);
    this.applyTheme(theme);
    
    // Migrate from localStorage to user preference if needed
    if (user && !user.theme) {
      const localTheme = this.getThemePreference();
      if (localTheme !== 'light') { // Don't migrate default theme
        this.updateUserTheme(user, localTheme).catch(error => {
          console.warn('Failed to migrate theme preference to user profile:', error);
        });
      }
    }
  }

  /**
   * Get current user's theme preference
   */
  getCurrentUserTheme(): ThemeMode {
    const currentUser = this.keycloakService.currentUser();
    return this.getUserTheme(currentUser);
  }
}